import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SHOT_CONFIG } from '../constants/shotConfig';

export interface AiProcessedPhoto {
  skinImprovement: string;
  characterize: string;
}

export interface SelectedPhoto {
  photoIndex: number;
  type: 'skinImprovement' | 'characterize';
}

export interface PhotoState {
  sessionId: number;
  photoCount: number;
  shotCount: number;
  videos: string[];
  photos: string[];
  aiProcessedPhotos: AiProcessedPhoto[];
  selectedPhotos: (SelectedPhoto | null)[];
  selectedFrameIndex: number;
  fourCutImage: string | null;
  qrCode: string | null;
  allowQrcode: boolean;
}

const initialState: PhotoState = {
  sessionId: 0,
  photoCount: 0,
  shotCount: SHOT_CONFIG.DEFAULT_SHOT_COUNT,
  videos: [],
  photos: [],
  aiProcessedPhotos: [],
  selectedPhotos: new Array(4).fill(null),
  selectedFrameIndex: 0,
  fourCutImage: null,
  qrCode: null,
  allowQrcode: true,
};

const photoSlice = createSlice({
  name: 'photo',
  initialState,
  reducers: {
    setFourCutImage(state, action: PayloadAction<string>) {
      state.fourCutImage = action.payload;
    },
    setPhotoCount(state, action: PayloadAction<number>) {
      state.photoCount = action.payload;
    },
    setShotCount(state, action: PayloadAction<number>) {
      state.shotCount = action.payload;
      state.aiProcessedPhotos = Array.from({ length: action.payload }, () => ({
        skinImprovement: '',
        characterize: '',
      }));
    },
    setPhotos(
      state,
      action: PayloadAction<{
        photos: string[];
      }>
    ) {
      state.photos = action.payload.photos;
    },
    setVideos(
      state,
      action: PayloadAction<{
        videos: string[];
      }>
    ) {
      state.videos = action.payload.videos;
    },
    setPhotosAndVideos(
      state,
      action: PayloadAction<{
        compositeImage: string;
        videos: string[];
        photos: string[];
      }>
    ) {
      state.videos = action.payload.videos;
      state.photos = action.payload.photos;
    },
    setAIProcessedPhotos(state, action: PayloadAction<AiProcessedPhoto[]>) {
      state.aiProcessedPhotos = action.payload;
    },
    updateAIProcessedPhoto: (
      state,
      action: PayloadAction<{
        index: number;
        type: keyof AiProcessedPhoto;
        photo: string;
      }>
    ) => {
      const { index, type, photo } = action.payload;

      if (index < 0 || index >= state.aiProcessedPhotos.length) {
        console.error(
          `유효하지 않은 인덱스: ${index} (최대: ${state.aiProcessedPhotos.length - 1}). 작업이 취소되었습니다.`
        );
        return;
      }

      if (!state.aiProcessedPhotos[index]) {
        state.aiProcessedPhotos[index] = { skinImprovement: '', characterize: '' };
      }

      state.aiProcessedPhotos[index][type] = photo;
    },
    setSelectedPhotos(state, action: PayloadAction<SelectedPhoto[]>) {
      state.selectedPhotos = action.payload;
    },
    setSelectedFrameIndex: (state, action: PayloadAction<number>) => {
      state.selectedFrameIndex = action.payload;
    },
    resizeSelectedPhotos: (state, action: PayloadAction<number>) => {
      const newSize = action.payload;
      state.selectedPhotos = new Array(newSize).fill(null);
    },
    setQrCode: (state, action: PayloadAction<string>) => {
      state.qrCode = action.payload;
    },
    setAllowQrcode: (state, action: PayloadAction<boolean>) => {
      state.allowQrcode = action.payload;
    },
    resetPhotoState: (state) => ({
      ...initialState,
      sessionId: state.sessionId + 1,
    }),
  },
});

export const {
  setFourCutImage,
  setPhotoCount,
  setShotCount,
  setPhotos,
  setVideos,
  setPhotosAndVideos,
  setAIProcessedPhotos,
  updateAIProcessedPhoto,
  setSelectedPhotos,
  setSelectedFrameIndex,
  resizeSelectedPhotos,
  setQrCode,
  setAllowQrcode,
  resetPhotoState,
} = photoSlice.actions;
export default photoSlice.reducer;
